import React from 'react';

import { Icon } from 'framework7-react';
import II18 from '../translate/II18';
import { isDocumentFormValid } from './formUtils';
import { isExternalApiValidationStampValid } from './externalApiValidation';
import { md5Password } from '../home/utils';
import { arrPushMany } from './immutable';
import { gaeApp } from './Analytics';

export const DOC_TYPE_SIGNATURE = 'SIGNATURE';
export const DOC_TYPE_INFORMATION = 'INFORMATION';
export const DOC_TYPE_IMMEDIATE_CONFIRMATION = 'IMMEDIATE_CONFIRMATION';
export const DOC_TYPE_ID_CHECK = 'ID_CHECK';
export const DOC_TYPE_FORM = 'FORM';
export const DOC_TYPE_PDF_FORM = 'FORM_PDF';
export const DOC_TYPE_DOCUMENT = 'DOCUMENT';
export const DOC_TYPE_PAYMENT = 'PAYMENT';

export const DOCUMENT_DISPLAY_MODE_INLINE = 'DOCUMENT_DISPLAY_MODE_INLINE';
export const DOCUMENT_DISPLAY_MODE_INPAGE = 'DOCUMENT_DISPLAY_MODE_INPAGE';
export const DOCUMENT_DISPLAY_MODE_DESKTOP_WINDOW =
  'DOCUMENT_DISPLAY_MODE_DESKTOP_WINDOW';
export const DOCUMENT_DISPLAY_MODE_DESKTOP_POPUP =
  'DOCUMENT_DISPLAY_MODE_DESKTOP_POPUP';
export const DOCUMENT_DISPLAY_MODE_MOBILE_WINDOW =
  'DOCUMENT_DISPLAY_MODE_MOBILE_WINDOW';

export const DOCUMENT_EXTERNAL_EVENTS = {
  NEXT_STEP: 'NEXT_STEP',
  PREV_STEP: 'PREV_STEP',
  NEXT_ELEMENT: 'NEXT_ELEMENT'
};

export const APP_EXTERNAL_EVENTS = {
  APP_NEXT_ACTION: 'APP_NEXT_ACTION',
  APP_PREV_ACTION: 'APP_PREV_ACTION'
};

export const defineLandscapeMode = () => {
  const s = defineScreenSize();
  return s.width > s.height;
};
export const defineScreenSize = () => {
  return {
    width: window.innerWidth || document.documentElement.clientWidth,
    height: window.innerHeight || document.documentElement.clientHeight
  };
};

export function getViewPortHeight() {
  return window.visualViewport
    ? window.visualViewport.height
    : window.innerHeight;
}

export function prepareScanUtils(iosVersion) {
  window.utils.prepareScanUtils();
}

export const isRequestedDocumentsHas = (documents, types) => {
  let has = false;
  documents.forEach(doc => (has = has || types.includes(doc.type)));
  return has;
};

export const getFirstRequestedDocument = documents => {
  return documents.entries().next().value[1];
};

export const getIcon = (icon, size, slot) => {
  if (icon.includes('fa-')) {
    return <Icon size={size} icon={'fas ' + icon} slot={slot} />;
  } else {
    return <Icon size={size} md={icon} ios={icon} slot={slot} />;
  }
};

const isNonEmptyFormFieldValue = value => {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    if ('code' in value || 'number' in value) {
      return Boolean(
        String(value.code || '').trim() && String(value.number || '').trim()
      );
    }
    return Object.values(value).some(v => isNonEmptyFormFieldValue(v));
  }
  return String(value).trim() !== '';
};

const collectFormInputChangeIds = forms => {
  const ids = [];
  if (!forms?.size) return ids;

  for (const [documentId, formData] of forms.entries()) {
    if (!formData?.size) continue;
    if ([...formData.values()].some(isNonEmptyFormFieldValue)) {
      ids.push(documentId);
    }
  }
  return ids;
};

const collectScannedDocumentChangeIds = scannedDocuments => {
  const ids = [];
  if (!scannedDocuments?.size) return ids;

  for (const [documentId, pages] of scannedDocuments.entries()) {
    if (pages?.size > 0) {
      ids.push(documentId);
    }
  }
  return ids;
};

const isMeaningfulDocumentStatus = status => {
  if (status === undefined || status === null) return false;
  if (typeof status === 'string') return status.trim() !== '';
  if (typeof status !== 'object') return true;

  return Object.entries(status).some(([, value]) => {
    if (value === undefined || value === null || value === '') return false;
    if (value === false) return false;
    return true;
  });
};

const collectDocumentStatusChangeIds = documentsStatus => {
  const ids = [];
  if (!documentsStatus?.size) return ids;

  for (const [documentId, status] of documentsStatus.entries()) {
    if (isMeaningfulDocumentStatus(status)) {
      ids.push(documentId);
    }
  }
  return ids;
};

/**
 * True if the user has entered any local progress that would be lost
 * when leaving the request (scan pages, form fields, statuses, rejects).
 * Expects raw redux state maps/collections (not prepared state).
 */
export const hasAnyDocumentInputChanges = (
  scannedDocuments,
  forms,
  documentsStatus,
  rejectedDocuments
) => {
  const reasons = [];

  if (rejectedDocuments?.size) {
    reasons.push({
      source: 'rejectedDocuments',
      size: rejectedDocuments.size,
      keys:
        rejectedDocuments instanceof Set
          ? [...rejectedDocuments]
          : rejectedDocuments
    });
  }

  const formIds = collectFormInputChangeIds(forms);
  if (formIds.length) {
    reasons.push({
      source: 'forms',
      size: formIds.length,
      keys: formIds
    });
  }

  const statusIds = collectDocumentStatusChangeIds(documentsStatus);
  if (statusIds.length) {
    reasons.push({
      source: 'documentsStatus',
      size: statusIds.length,
      keys: statusIds
    });
  }

  const scannedIds = collectScannedDocumentChangeIds(scannedDocuments);
  if (scannedIds.length) {
    reasons.push({
      source: 'scannedDocuments',
      size: scannedIds.length,
      keys: scannedIds
    });
  }

  const hasChanges = reasons.length > 0;

  if (hasChanges) {
    gaeApp(
      'DOCUMENT_INPUT_HAS_CHANGES',
      reasons
        .map(({ source, size, keys }) => `${source}:${size}[${keys.join(',')}]`)
        .join(';')
    );
    reasons.forEach(({ source, size, keys }) => {
      gaeApp(
        'DOCUMENT_INPUT_CHANGES_DETAIL',
        source,
        `${size}|${keys.join(',')}`
      );
    });
  } else {
    gaeApp('DOCUMENT_INPUT_HAS_NO_CHANGES');
  }

  return hasChanges;
};

export const isDocumentSubmitted = document => {
  return document.state === 'SUBMITTED' || document.state === 'ACCEPTED';
};

export const isDocumentRejected = document => {
  return document.state === 'REJECTED';
};

export const isSomeDocumentSubmitted = requestedDocuments => {
  return Array.from(requestedDocuments.values()).some(d =>
    isDocumentSubmitted(d)
  );
};

export const onlyInfoDocuments = requestedDocumentsMap =>
  Array.from(requestedDocumentsMap.values()).every(
    doc => doc.type === DOC_TYPE_INFORMATION
  );

export const isDocumentHasProblems = (
  errorSentKeys,
  document,
  scannedPages
) => {
  return (
    scannedPages?.size &&
    Array.from(scannedPages.keys()).some(pageId =>
      errorSentKeys?.has(document.id + pageId)
    )
  );
};

export const isStepHasProblems = (
  errorSentKeys,
  documents,
  documentsScannedPages
) => {
  return documents.some(doc =>
    isDocumentHasProblems(errorSentKeys, doc, documentsScannedPages.get(doc.id))
  );
};

export const isDocumentReady = (
  document,
  scannedPages,
  formData,
  documentStatus,
  requestedDocuments
) => {
  const hasScannedPages = Boolean(scannedPages && scannedPages.size);

  switch (document.type) {
    case DOC_TYPE_ID_CHECK: {
      const idCheckSteps = prepareIdCheckSteps(
        document,
        scannedPages,
        documentStatus
      );
      return {
        done: idCheckSteps.every(step => step.passed),
        status: idCheckSteps.map(step => `${step.stepTitle}: ${step.passed}`)
      };
    }
    case DOC_TYPE_FORM:
    case DOC_TYPE_PDF_FORM: {
      const formValid = Boolean(
        formData &&
          isDocumentFormValid(
            document.formStructure,
            formData,
            requestedDocuments,
            document.id
          )
      );
      if (!formValid) {
        return { done: false };
      }
      // External API validation stamp required when configured (Done / pre-send).
      return {
        done: isExternalApiValidationStampValid(
          document,
          document.formStructure,
          formData
        )
      };
    }
    case DOC_TYPE_PAYMENT:
      return {
        done: Boolean(documentStatus)
      };
    case DOC_TYPE_IMMEDIATE_CONFIRMATION:
      return {
        done:
          documentStatus?.confirmed === true &&
          (!document.attributes.downloadRequired ||
            documentStatus?.downloaded === true)
      };
    case DOC_TYPE_INFORMATION:
      return {
        done:
          !document.attributes.downloadRequired ||
          documentStatus?.downloaded === true
      };
    default:
      return {
        done: hasScannedPages
      };
  }
};

/**
 *
 * @param {Array} requestedDocuments
 * @param {boolean} scanEnded
 * @param {boolean} requestSubmitted
 * @returns {boolean}
 */
export const isSkipDocumentListMode = (requestedDocuments, scanEnded) => {
  if (!requestedDocuments || requestedDocuments.length !== 1) return false;

  return !scanEnded;
};

export const payedValue = (document, documentsStatus) =>
  document &&
  ((documentsStatus.has(document.id) && documentsStatus.get(document.id)) ||
    document.state === 'SUBMITTED');

export const getColorBrightness = color => {
  let [r, g, b] = getRGBArray(color);
  if (typeof r != 'undefined') return (r * 299 + g * 587 + b * 114) / 1000;
};

function getRGBArray(color) {
  color = '' + color;
  let isHEX = color.indexOf('#') === 0,
    isRGB = color.indexOf('rgb') === 0;
  let r, g, b;

  if (isHEX) {
    const hasFullSpec = color.length === 7;
    let rgbArray = color.substr(1).match(hasFullSpec ? /(\S{2})/g : /(\S{1})/g);
    if (rgbArray) {
      r = parseInt(rgbArray[0] + (hasFullSpec ? '' : rgbArray[0]), 16);
      g = parseInt(rgbArray[1] + (hasFullSpec ? '' : rgbArray[1]), 16);
      b = parseInt(rgbArray[2] + (hasFullSpec ? '' : rgbArray[2]), 16);
    }
    return [r, g, b];
  }
  if (isRGB) {
    return color.match(/(\d+){3}/g);
  }
}

function getLowestMiddleHighest(rgbIntArray) {
  let highest = { val: -1, index: -1 };
  let lowest = { val: Infinity, index: -1 };

  rgbIntArray.forEach((val, index) => {
    if (val > highest.val) {
      highest = { val: val, index: index };
    }
    if (val < lowest.val) {
      lowest = { val: val, index: index };
    }
  });

  if (lowest.index === highest.index) {
    lowest.index = highest.index + 1;
  }

  let middle = { index: 3 - highest.index - lowest.index };
  middle.val = rgbIntArray[middle.index];
  return [lowest, middle, highest];
}

function componentToHex(c) {
  let hex = c.toString(16);
  return hex.length === 1 ? '0' + hex : hex;
}

function rgbToHex(r, g, b) {
  return '#' + componentToHex(r) + componentToHex(g) + componentToHex(b);
}

export const decreaseColor = (color, on) => {
  // Our rgb to int array function again
  const rgbIntArray = getRGBArray(color);
  //grab the values in order of magnitude
  //this uses the function from the saturate function
  const [lowest, middle, highest] = getLowestMiddleHighest(rgbIntArray);

  if (highest.val === 0) {
    return color;
  }

  const returnArray = [];

  returnArray[highest.index] = highest.val - Math.min(highest.val, on || 35);
  const decreaseFraction =
    (highest.val - returnArray[highest.index]) / highest.val;
  returnArray[middle.index] = middle.val - middle.val * decreaseFraction;
  returnArray[lowest.index] = lowest.val - lowest.val * decreaseFraction;

  // Convert the array back into an rgb string
  return rgbToHex(
    Math.round(returnArray[0]),
    Math.round(returnArray[1]),
    Math.round(returnArray[2])
  );
};

export const shallowEqual = (objA, objB, consoleOut) => {
  if (objA === objB) {
    return true;
  }

  if (
    typeof objA !== 'object' ||
    objA === null ||
    typeof objB !== 'object' ||
    objB === null
  ) {
    return false;
  }

  if (objA instanceof Map && objB instanceof Map) {
    return mapsEqual(objA, objB, consoleOut);
  }

  if (Array.isArray(objA) && Array.isArray(objB)) {
    return arraysEqual(objA, objB, consoleOut);
  }

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    consoleOut && console.log('keys len changed');
    return false;
  }

  // Test for A's keys different from B.
  const bHasOwnProperty = hasOwnProperty.bind(objB);
  for (let i = 0; i < keysA.length; i++) {
    if (!bHasOwnProperty(keysA[i]) || objA[keysA[i]] !== objB[keysA[i]]) {
      if (typeof objA[keysA[i]] === 'object') {
        if (!shallowEqual(objA[keysA[i]], objB[keysA[i]], consoleOut)) {
          return false;
        }
      } else {
        consoleOut && console.log(`"${keysA[i]}" changed`);
        return false;
      }
    }
  }

  return true;
};

function mapsEqual(map1, map2, consoleOut) {
  if (map1.size !== map2.size) {
    consoleOut && console.log('Maps length is different');
    return false;
  }

  for (var [key, map1Val] of map1) {
    const map2Val = map2.get(key);
    if (!shallowEqual(map1Val, map2Val)) return false;
  }
  return true;
}

function arraysEqual(arr1, arr2, consoleOut) {
  if (arr1.length !== arr2.length) {
    consoleOut && console.log('Arrays length is different');
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    if (!shallowEqual(arr1[i], arr2[i])) return false;
  }
  return true;
}

export const onBrowserClose = e => {
  const pageHasAlreadyBeenForceRefreshed = JSON.parse(
    window.localStorage.getItem('page-has-been-force-refreshed') || 'false'
  );
  if (!pageHasAlreadyBeenForceRefreshed) {
    e.preventDefault();
    e.returnValue = '';
  }
};

export const isElementVisible = el => {
  const rect = el.getBoundingClientRect(),
    vWidth = window.innerWidth || document.documentElement.clientWidth,
    vHeight = window.innerHeight || document.documentElement.clientHeight,
    efp = function(x, y) {
      return document.elementFromPoint(x, y);
    };

  // Return false if it's not in the viewport
  if (
    rect.right < 0 ||
    rect.bottom < 0 ||
    rect.left > vWidth ||
    rect.top > vHeight
  )
    return false;

  // Return true if any of its four corners are visible
  return (
    el.contains(efp(rect.left, rect.top)) ||
    el.contains(efp(rect.right, rect.top)) ||
    el.contains(efp(rect.right, rect.bottom)) ||
    el.contains(efp(rect.left, rect.bottom))
  );
};

export const POSITION_OUTSIDE_TOP = -1;
export const POSITION_OUTSIDE_BOTTOM = 1;
export const POSITION_INSIDE = 0;

export const defineElementLocationInViewport = (elementId, viewportId) => {
  const element = document.getElementById(elementId);
  if (element) {
    const viewportContainer = document.getElementById(viewportId);
    const rect = element.getBoundingClientRect();
    return rect.top < 0
      ? POSITION_OUTSIDE_TOP
      : rect.top > viewportContainer.clientHeight
      ? POSITION_OUTSIDE_BOTTOM
      : POSITION_INSIDE;
  }
};

export function longestWord(str) {
  return str
    .split(' ')
    .reduce((champ, contender) =>
      contender.length > champ.length ? contender : champ
    );
}

export function emailIsValid(email) {
  const regExp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  const nEmail =
    email && email.normalize('NFD').replace(/[\u0300-\u036f,\u00DF]/g, '');
  return email === nEmail && regExp.test(email);
  //return email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export function phoneIsValid(phone) {
  // noinspection RegExpRedundantEscape
  return (
    phone &&
    // eslint-disable-next-line no-useless-escape
    /^[+|00]?[0-9]{7,15}$/im.test(phone)
    ///^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(phone)
  );
}

export function userNameIsValid(userName) {
  return userName && userName.trim();
}

export function userFiledsAreValid(userFields) {
  return userFields.length === 0 || userFields.every(field => field.value);
}

export function userEmailOrMobileIsValid(userEmail, smsDisabled) {
  return emailIsValid(userEmail) || (!smsDisabled && phoneIsValid(userEmail));
}

export const isNotEmptyArray = arr => Array.isArray(arr) && arr.length;

export const mapToArray = map =>
  map ? Array.from(map).map(entry => entry[1]) : null;

export const arrayToMap = (arr, defineKey, defineValue) => {
  const result = new Map();
  arr.forEach((element, index) => {
    result.set(
      defineKey(element, index),
      defineValue ? defineValue(element, index) : element
    );
  });
  return result;
};

export const MODE_PHOTO = 'modePhoto';
export const MODE_CAMERA_FRONT = 'modeCameraFront';
export const MODE_CAMERA_BACK = 'modeCameraBack';

export const ID_CHECK_TYPE_PASS = 'passport';
export const ID_CHECK_TYPE_ID_CARD = 'identificationCard';
export const ID_CHECK_TYPE_DRIVER_LIC = 'driversLicense';
export const ID_CHECK_STEP_DOC_TYPE = 'selectDocType';
export const ID_CHECK_STEP_SELFIE = 'selfie';

export const ID_CHECK_STEP_DOC_FRONT = 'docFront';
export const ID_CHECK_STEP_DOC_BACK = 'docBack';
export const ID_CHECK_STEP_DOC_LIVE = 'docLive';
export const ID_CHECK_STEP_DOC_SELFIE = 'selfie';

export const ID_CHECK_PAGES_IDS = [
  ID_CHECK_STEP_DOC_FRONT,
  ID_CHECK_STEP_DOC_BACK,
  ID_CHECK_STEP_DOC_LIVE,
  ID_CHECK_STEP_DOC_SELFIE
];

export const getIdCheckPossibleDocuments = documentItem => {
  const possibleDocuments = [];
  if (documentItem.attributes.acceptableIds?.identificationCard) {
    possibleDocuments.push(ID_CHECK_TYPE_ID_CARD);
  }
  if (documentItem.attributes.acceptableIds?.passport) {
    possibleDocuments.push(ID_CHECK_TYPE_PASS);
  }
  if (documentItem.attributes.acceptableIds?.driversLicense) {
    possibleDocuments.push(ID_CHECK_TYPE_DRIVER_LIC);
  }
  return possibleDocuments;
};

export const ID_CHECK_DOC_TYPE_ICONS = {
  [ID_CHECK_TYPE_PASS]: 'fas fa-passport',
  [ID_CHECK_TYPE_ID_CARD]: 'fas fa-id-card',
  [ID_CHECK_TYPE_DRIVER_LIC]: 'fas fa-address-card'
};

export const defineIdCheckDocumentTypeFromStatus = (
  idCheckDocument,
  idCheckStatus
) => {
  const possibleDocuments = getIdCheckPossibleDocuments(idCheckDocument);
  return possibleDocuments.length === 1
    ? possibleDocuments[0]
    : idCheckStatus?.idCheckDocumentType;
};
export const prepareIdCheckSteps = (
  idCheckDocument,
  idCheckScannedPages,
  idCheckStatus
) => {
  const possibleDocuments = getIdCheckPossibleDocuments(idCheckDocument);
  const selectedDocument = defineIdCheckDocumentTypeFromStatus(
    idCheckDocument,
    idCheckStatus
  );
  const backPhotoRequired = selectedDocument === ID_CHECK_TYPE_ID_CARD;
  const documentPhotoRequired = true;
  const documentLivePhotoRequired =
    idCheckDocument.attributes.livePhotoRequired;
  const selfieRequired = idCheckDocument.attributes.selfieRequired;

  const docFrontSideUrl =
    idCheckScannedPages && idCheckScannedPages.get(ID_CHECK_STEP_DOC_FRONT);
  const docBackSideUrl =
    idCheckScannedPages && idCheckScannedPages.get(ID_CHECK_STEP_DOC_BACK);
  const docLiveUrl =
    idCheckScannedPages && idCheckScannedPages.get(ID_CHECK_STEP_DOC_LIVE);
  const selfieUrl =
    idCheckScannedPages && idCheckScannedPages.get(ID_CHECK_STEP_SELFIE);

  const steps = [];
  const selectedDocumentTitle = II18.get(
    'id_check_doc_type_' + selectedDocument
  );

  //window.isDesktop
  possibleDocuments.length > 1 &&
    steps.push({
      info: selectedDocumentTitle,
      passed: Boolean(selectedDocument) || idCheckDocument.submitted,
      id: ID_CHECK_STEP_DOC_TYPE,
      stepTitle: II18.get('idchek_document_select_doc_type_step_title'),
      documentTitle: II18.get('idchek_document_select_doc_type_title'),
      documentShortDesc: II18.get('idchek_document_select_doc_type_desc')
    });

  documentPhotoRequired &&
    steps.push({
      url: docFrontSideUrl,
      passed: Boolean(docFrontSideUrl) || idCheckDocument.submitted,
      id: ID_CHECK_STEP_DOC_FRONT,
      loadMode: MODE_PHOTO,
      stepTitle: II18.get(
        selectedDocument === ID_CHECK_TYPE_PASS
          ? 'idchek_document_doc_photo_step_title'
          : 'idchek_document_front_side_step_title'
      ),
      documentTitle:
        selectedDocumentTitle +
        (backPhotoRequired
          ? ' - ' + II18.get('idchek_document_front_side_title')
          : ''),
      documentShortDesc: II18.get(
        'idchek_document_front_side_desc_' + selectedDocument
      ),
      icon: selectedDocument ? ID_CHECK_DOC_TYPE_ICONS[selectedDocument] : ''
    });

  backPhotoRequired &&
    steps.push({
      url: docBackSideUrl,
      passed: Boolean(docBackSideUrl) || idCheckDocument.submitted,
      id: ID_CHECK_STEP_DOC_BACK,
      loadMode: MODE_PHOTO,
      stepTitle: II18.get('idchek_document_back_side_step_title'),
      documentTitle:
        selectedDocumentTitle +
        ' - ' +
        II18.get('idchek_document_back_side_title'),
      documentShortDesc: II18.get(
        'idchek_document_back_side_desc_' + selectedDocument
      ),
      icon: 'fas fa-money-check'
    });

  documentLivePhotoRequired &&
    steps.push({
      url: docLiveUrl,
      passed: Boolean(docLiveUrl) || idCheckDocument.submitted,
      id: ID_CHECK_STEP_DOC_LIVE,
      webRTC: true,
      loadMode: MODE_CAMERA_BACK,
      stepTitle: II18.get('idchek_document_live_photo_step_title'),
      documentTitle: II18.get('idchek_document_live_photo_title'),
      documentShortDesc: II18.get(
        'idchek_document_live_photo_desc',
        selectedDocumentTitle
      ),
      icon: 'material:videocam'
    });

  selfieRequired &&
    steps.push({
      url: selfieUrl,
      webRTC: true,
      passed: Boolean(selfieUrl) || idCheckDocument.submitted,
      id: ID_CHECK_STEP_SELFIE,
      loadMode: MODE_CAMERA_FRONT,
      stepTitle: II18.get('idcheck_page_selfie_step_title'),
      documentTitle: II18.get('idcheck_doselfie_item_title'),
      documentShortDesc: II18.get('idcheck_doselfie_item_message'),
      icon: 'material:person'
    });

  return steps;
};

export const getRequestDocumentsInfo = (requestPages, requestedDocuments) => {
  let requestStatInfo = `\n`;
  requestPages
    ? requestPages.forEach((page, index) => {
        requestStatInfo += `\n Page ${index + 1}: ${page.title} [${
          page.pageId
        }; completeOnExit: ${page.pageSettings?.completeOnExit}]`;
        page.documents.forEach(docId => {
          const doc = requestedDocuments.find(d => d.id === docId);
          if (doc) {
            requestStatInfo += '\n - ' + getDocumentInfo(doc);
          }
        });
      })
    : requestedDocuments.forEach(
        doc => (requestStatInfo += '\n - ' + getDocumentInfo(doc))
      );

  return requestStatInfo + '\n\n';
};

export const getDocumentInfo = _doc => {
  const doc = { ..._doc };

  const submitted = isDocumentSubmitted(doc);
  delete doc.portalDocumentId;
  delete doc.templateId;
  delete doc.state;
  delete doc.stats;
  delete doc.submitted;

  const commonInfo =
    (submitted ? '[SUBMITTED]' : '') +
    `${doc.name}  (${doc.type} - id: ${doc.id.substring(
      0,
      4
    )}... checkSumm: [${md5Password(
      JSON.stringify(doc).replace(
        /(https?:\/\/)?([\da-z\\.-]+)\.([a-z\\.]{2,6})([\\/\w \\.\\?&-~]*)/g,
        ''
      )
    )}])`;

  switch (doc.type) {
    case DOC_TYPE_ID_CHECK: {
      const idCheckSteps = prepareIdCheckSteps(doc);
      return commonInfo + idCheckSteps.map(s => `\n -- ${s.stepTitle}`);
    }
    default:
      return commonInfo;
  }
};

export const onEnterClick = (e, onClick) => {
  if (e.keyCode === 13) {
    e.preventDefault();
    onClick();
  }
};

/** First visible required document that is not done/rejected (optional docs excluded). */
export const findNotReadyDocumentInList = (documents = []) =>
  documents.find(
    document => !(document.optional || document.done || document.rejected)
  );

export const collectPartialCompleteDocuments = (
  sendFilesTillPage,
  requestPages,
  requestedDocuments
) => {
  const completePageIndex = requestPages.findIndex(
    p => p.pageId === sendFilesTillPage
  );

  const collectedDocsOnlyFromPagesToSend =
    sendFilesTillPage &&
    requestPages?.reduce((acc, page, index) => {
      return index <= completePageIndex
        ? arrPushMany(acc, page.documents)
        : acc;
    }, []);

  return {
    completePageIndex,
    documentsToSend: requestedDocuments.filter(
      doc =>
        !collectedDocsOnlyFromPagesToSend ||
        collectedDocsOnlyFromPagesToSend.find(d => d.id === doc.id)
    )
  };
};
